import { ref } from 'vue'
import { describeClientVersion, latestReleaseSourceFor, type LatestReleaseSource, type LatestReleases } from '~/utils/clientLatestVersion'

type Heartbeat = { version?: string; platform?: string }
type ReleaseList = Record<LatestReleaseSource, LatestReleases | { error: string }>
type ListState = { state: 'pending' } | { state: 'done'; releases: ReleaseList } | { state: 'error'; message: string }

export type ClientLatestVersionView = { status: string; text: string }

const RELOAD_AFTER_MS = 5 * 60_000

// Shared by every admin component on the page. PandaUnion caches the TestFlight part.
const list = ref<ListState | null>(null)
let loadedAt = 0

const load = () => {
  if (list.value?.state === 'pending' || (list.value && Date.now() - loadedAt < RELOAD_AFTER_MS)) return
  list.value = { state: 'pending' }
  loadedAt = Date.now()
  useAdmin.getClientLatestReleases().then(
    (releases) => {
      list.value = { state: 'done', releases }
    },
    (error) => {
      list.value = { state: 'error', message: error?.message || String(error) }
    },
  )
}

export const useClientLatestVersion = () => {
  /** Starts loading the release list when a heartbeat has a published channel. Call it from a watcher, not from render. */
  const loadFor = (heartbeats: Heartbeat[]) => {
    if (heartbeats.some((heartbeat) => latestReleaseSourceFor(heartbeat))) load()
  }

  /** The verdict text for one heartbeat; null when the platform has no published channel. */
  const verdictFor = (heartbeat: Heartbeat): ClientLatestVersionView | null => {
    const source = latestReleaseSourceFor(heartbeat)
    if (!source) return null
    const current = list.value
    if (!current || current.state === 'pending') return { status: 'pending', text: '正在读取最新版本…' }
    if (current.state === 'error') return { status: 'error', text: `最新版本读取失败：${current.message}` }
    const releases = current.releases[source]
    if (!releases) return { status: 'error', text: `最新版本读取失败：接口没有 ${source}` }
    if ('error' in releases) return { status: 'error', text: `最新版本读取失败：${releases.error}` }
    return describeClientVersion(heartbeat, releases)
  }

  return { loadFor, verdictFor }
}
